import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWaiterStore } from '../store/waiterStore';
import { getMenu } from '../../api/restaurant/menuApi';
import type { WaiterCartItem } from '../types/waiter';

interface MenuItem {
  id: string;
  name: string;
  price: number;
  discount?: number;
  description?: string;
  imageUrls?: string[];
  schemeId?: string;
  measure?: { value: number; unit: string };
  isContainInStopList?: any[];
}

interface MenuCategory {
  id: string;
  name: string;
  items: MenuItem[];
}

export const WaiterMenuPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { client, cart, addToCart, removeFromCart, updateQuantity, tableNumber, setTableNumber } = useWaiterStore();
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showCart, setShowCart] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadMenu();
  }, []);

  const loadMenu = async () => {
    try {
      setLoading(true);
      const response = await getMenu();

      // Структура: response.result.result.{categories, products}
      if (response.success && response.result && response.result.result) {
        const menuData = response.result.result;

        const excludedNames = ['кухня', 'модификаторы', 'допы кухня'];

        // Категории — отдельный массив
        const allCategories = (menuData.categories || []).filter(
          (c: any, i: number, self: any[]) => i === self.findIndex((x: any) => x.id === c.id)
        );

        // Исключаем служебные категории
        const excludedIds = new Set<string>();
        allCategories.forEach((cat: any) => {
          if (excludedNames.some(n => cat.name?.toLowerCase().includes(n))) {
            excludedIds.add(cat.id);
          }
        });
        const filteredCategories = allCategories.filter((c: any) => !excludedIds.has(c.id));

        // Продукты — отдельный массив, привязаны через categoryId
        const allProducts = (menuData.products || []).filter(
          (p: any, i: number, self: any[]) => i === self.findIndex((x: any) => x.id === p.id)
        );

        // Группируем продукты по categoryId
        const productsByCategory = new Map<string, MenuItem[]>();
        for (const item of allProducts) {
          const stopped = item.isContainInStopList && item.isContainInStopList.length > 0;
          if (stopped || item.disabled) continue;

          const menuItem: MenuItem = {
            id: item.id,
            name: item.name,
            price: item.price || 0,
            discount: item.discount || 0,
            description: item.description || '',
            imageUrls: item.imageUrls || [],
            schemeId: item.schemeId || '',
            measure: item.measure,
            isContainInStopList: item.isContainInStopList,
          };

          const catId = item.categoryId || '';
          if (!productsByCategory.has(catId)) {
            productsByCategory.set(catId, []);
          }
          productsByCategory.get(catId)!.push(menuItem);
        }

        // Категории с продуктами
        const cats: MenuCategory[] = [];
        for (const cat of filteredCategories) {
          const items = productsByCategory.get(cat.id) || [];
          if (items.length > 0) {
            cats.push({ id: cat.id, name: cat.name, items });
          }
        }

        setCategories(cats);
        if (cats.length > 0) setActiveCategory(cats[0].id);
      }
    } catch (err) {
      console.error('Failed to load menu:', err);
    } finally {
      setLoading(false);
    }
  };

  const cartTotal = cart.reduce((sum, i) => sum + (i.price - (i.discount || 0)) * i.quantity, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  const getCartQty = (itemId: string) => {
    const item = cart.find(i => i.id === itemId);
    return item ? item.quantity : 0;
  };

  const handleAddItem = (item: MenuItem) => {
    const cartItem: WaiterCartItem = {
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
      discount: item.discount,
      schemeId: item.schemeId,
      description: item.description,
      imageUrls: item.imageUrls,
      measure: item.measure,
    };
    addToCart(cartItem);
  };

  const filteredItems = () => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const all: MenuItem[] = [];
      categories.forEach(c => c.items.forEach(i => {
        if (i.name.toLowerCase().includes(q)) all.push(i);
      }));
      return all;
    }
    const cat = categories.find(c => c.id === activeCategory);
    return cat ? cat.items : [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center text-slate-400">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent mx-auto mb-3" />
          <p>Загрузка меню...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(`/waiter/client/${clientId}`)}
          className="flex items-center text-slate-400 hover:text-white transition-colors"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {client?.name || 'Гость'}
        </button>

        {/* Table number */}
        <div className="flex items-center space-x-2">
          <span className="text-slate-400 text-sm">Стол:</span>
          <input
            type="number"
            min="1"
            max="99"
            value={tableNumber}
            onChange={(e) => setTableNumber(parseInt(e.target.value) || 1)}
            className="w-14 px-2 py-1 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white text-center text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
          />
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-3 w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Поиск блюда..."
          className="w-full pl-10 pr-4 py-2.5 bg-slate-800/60 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
        />
      </div>

      {/* Categories */}
      {!searchQuery && (
        <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-hide">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeCategory === cat.id
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-800/60 text-slate-400 hover:text-white border border-slate-700/50'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Menu items */}
      <div className="space-y-2 pb-24">
        {filteredItems().map(item => {
          const qty = getCartQty(item.id);
          const finalPrice = item.price - (item.discount || 0);

          return (
            <div
              key={item.id}
              className="bg-slate-800/60 rounded-xl border border-slate-700/50 p-3 flex items-center justify-between"
            >
              <div className="flex-1 min-w-0 mr-3">
                <p className="text-white text-sm font-medium truncate">{item.name}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-emerald-400 font-semibold text-sm">{finalPrice} ₽</span>
                  {item.discount ? (
                    <span className="text-slate-500 text-xs line-through">{item.price} ₽</span>
                  ) : null}
                  {item.measure && (
                    <span className="text-slate-500 text-xs">{item.measure.value} {item.measure.unit}</span>
                  )}
                </div>
              </div>

              {qty === 0 ? (
                <button
                  onClick={() => handleAddItem(item)}
                  className="px-3 py-1.5 bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-600/30 transition-colors"
                >
                  +
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => updateQuantity(item.id, qty - 1)}
                    className="w-8 h-8 rounded-lg bg-slate-700/50 text-white flex items-center justify-center hover:bg-slate-700"
                  >
                    -
                  </button>
                  <span className="text-white font-medium w-6 text-center">{qty}</span>
                  <button
                    onClick={() => updateQuantity(item.id, qty + 1)}
                    className="w-8 h-8 rounded-lg bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 flex items-center justify-center hover:bg-emerald-600/30"
                  >
                    +
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Cart bar */}
      {cartCount > 0 && (
        <div className="fixed bottom-16 left-0 right-0 px-4 pb-2 z-40">
          <button
            onClick={() => navigate('/waiter/confirm')}
            className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-between px-5 shadow-lg shadow-emerald-600/20"
          >
            <span className="flex items-center space-x-2">
              <span className="bg-white/20 px-2 py-0.5 rounded-md text-sm">{cartCount}</span>
              <span>Оформить заказ</span>
            </span>
            <span>{cartTotal} ₽</span>
          </button>
        </div>
      )}
    </div>
  );
};
